// src/services/sourceDocExport.js
//
// SourceX — export an assembled source document as a clean, printable page.
//
// This is PRESENTATION ONLY. It never changes the template, the assembly, or any
// clinical data — it renders one visit's questions as a blank, fillable source
// document with a repeating header (study/site/PI + patient identifiers) and
// footer (version + date), then hands it to the browser to print or "Save as
// PDF". Rendering the real questions as HTML and letting the browser paginate is
// far more faithful than redrawing the document into a canvas: option lists,
// wide tables, required markers and conditional notes all survive exactly.
//
// Pure and framework-free (returns an HTML string), so it is unit-testable and
// carries no React/Firebase coupling.

import {
  isSourceOnlyType,
  columnPrintsBoxes,
  columnIsCheckbox,
  columnChoices,
  cellSelection,
  markStyleFor,
  columnHeaderGroups,
  checkboxIsTicked,
  isCompositeTable,
  tablePrintRowCount,
  tableRowHeightPt,
  DEFAULT_ROW_HEIGHT_PT,
  columnWidthPercents,
  eventIsLog,
  eventIsCriteria,
  eventIsLandscape,
  emphasisParts,
  criterionLines,
  splitThresholdFor,
} from "./templateSchema";
import { LOG_CLOSING_LABEL } from "./sourceLogTemplates";
import { ELIGIBILITY_CLOSING, CRITERIA_PAGE_TITLE } from "./eligibilityChecklist";

const esc = (s) =>
  String(s == null ? "" : s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

// A blank fill line — the handwriting space on a paper source. Measured in
// UNDERSCORES, not CSS width, because that is literally what Word writes: the
// same count here in the same font gives the same line, so the two documents
// break lines in the same places instead of merely looking similar.
const blank = (n = 16) => `<span class="blank">${"_".repeat(n)}</span>`;

// The two further write-in lines a Long text field gets beneath its first.
// See the TextArea case in answerArea.
const LONG_TEXT_EXTRA_LINES = `<div class="long-line">${"_".repeat(96)}</div><div class="long-line">${"_".repeat(96)}</div>`;

// A recorded answer written on the line (patient export). Bold, no rule under
// it — same as Word's answerText.
const filled = (text) => `<span class="filled">${esc(text)}</span>`;

// The mark before a choice. Square for a tick-any list (&#9744; empty ballot
// box, &#9745; ticked); round for a pick-one list (&#9675; / &#9679;) — see
// choiceMarkFor. The shape is the only thing on paper that says whether more
// than one may be marked.
const mark = (checked, style) =>
  style === "radio"
    ? checked
      ? "&#9679;"
      : "&#9675;"
    : checked
    ? "&#9745;"
    : "&#9744;";
const choice = (label, checked = false, style = "box") =>
  `<span class="cb">${mark(checked, style)} ${esc(label)}</span>`;

const hasAnswer = (v) =>
  v !== undefined &&
  v !== null &&
  v !== "" &&
  !(Array.isArray(v) && v.length === 0);
const isCheckedVal = (v) => v === true || v === "true";

/**
 * Resolve a question's `visibleWhen` into a short human instruction, using the
 * event's other questions for the controlling label. A printed source cannot
 * branch dynamically, so a conditional field is SHOWN with its condition stated
 * ("only if …") — the accurate paper equivalent of skip logic.
 */
const conditionText = (cond, labelById) => {
  if (!cond || typeof cond !== "object") return "";
  const join = (arr, sep) => arr.map((c) => conditionText(c, labelById)).filter(Boolean).join(sep);
  if (Array.isArray(cond.all)) return join(cond.all, " and ");
  if (Array.isArray(cond.any)) return join(cond.any, " or ");
  if (cond.not) {
    const inner = conditionText(cond.not, labelById);
    return inner ? `not (${inner})` : "";
  }
  if (cond.questionId) {
    const lbl = labelById.get(cond.questionId) || "a previous answer";
    const op = { eq: "=", neq: "≠", gt: ">", gte: "≥", lt: "<", lte: "≤" }[cond.op] || cond.op;
    const val = Array.isArray(cond.value) ? cond.value.join(", ") : cond.value;
    if (cond.op === "isBlank") return `${lbl} is blank`;
    if (cond.op === "notBlank") return `${lbl} is answered`;
    return `${lbl} ${op} ${esc(val)}`;
  }
  return "";
};

/**
 * The fillable area for one non-heading question, by type.
 * When `filled` is true (patient export) the recorded answer is shown — ticked
 * boxes, written values, filled table rows. When false (blank template export)
 * the output is byte-identical to the original blank form.
 */
const answerArea = (q, withAnswers = false) => {
  const type = q.type;
  const opts = Array.isArray(q.options) ? q.options : [];
  const v = q.value;
  // A coordinator-set default for the BLANK form — never a patient's actual
  // answer, which always wins once real answers are printing. Same idea as a
  // Table's prefillRows, just for a single (non-Table) question.
  const def = !withAnswers ? cellSelection(q.defaultValue) : [];
  const on = (opt) => (withAnswers ? v === opt : def.includes(String(opt)));
  const defText = () =>
    !withAnswers && q.defaultValue
      ? filled(Array.isArray(q.defaultValue) ? q.defaultValue.join(", ") : q.defaultValue)
      : null;
  switch (type) {
    case "YesNo/Maybe":
      return `<div class="opts">${["Yes", "No", "N/A"]
        .map((o) => choice(o, on(o)))
        .join("")}</div>`;
    case "Checkbox": {
      // ONE box, no labels. It used to print "☐ Yes  ☐ No", which is a
      // different question — a checkbox is ticked or it is not, and offering a
      // second box to say "no" invites both being left blank and neither
      // meaning anything. A Yes/No/N/A field is its own type for that.
      // checkboxIsTicked, not a bare === true: a pre-check saved while this
      // printed as Yes/No reads "Yes", and that box still has to come out
      // ticked. See templateSchema.
      const on = checkboxIsTicked(withAnswers ? v : q.defaultValue);
      return `<div class="opts"><span class="cb">${mark(on, "box")}</span></div>`;
    }
    case "Select":
      // Round marks — a dropdown is pick-ONE, and square boxes read as
      // "check all that apply" on paper. See choiceMarkFor. A question can
      // override that where the printed form it replaces draws boxes anyway.
      return `<div class="opts">${opts
        .map((o) => choice(o, on(o), markStyleFor(q)))
        .join("")}</div>`;
    case "MultiSelect": {
      const sel = withAnswers ? (Array.isArray(v) ? v : []) : def;
      return `<div class="opts"><span class="hint">(check all that apply)</span> ${opts
        .map((o) => choice(o, sel.includes(o)))
        .join("")}</div>`;
    }
    // Underscore counts mirrored one-for-one from sourceDocDocx's answerRuns,
    // so a field takes the same run of line in both documents.
    case "Number":
      return `${withAnswers && hasAnswer(v) ? filled(v) : defText() || blank(8)}${
        q.unit ? `<span class="unit">${esc(q.unit)}</span>` : ""
      }`;
    case "Date":
      return `${
        withAnswers && hasAnswer(v) ? filled(v) : defText() || blank(14)
      }<span class="hint">(DD-Mon-YYYY)</span>`;
    case "Time":
      return `${
        withAnswers && hasAnswer(v) ? filled(v) : defText() || blank(8)
      }<span class="hint">(HH:MM)</span>`;
    case "DateTime":
      return `${
        withAnswers && hasAnswer(v) ? filled(v) : defText() || blank(20)
      }<span class="hint">(DD-Mon-YYYY HH:MM)</span>`;
    case "TextArea":
      // Three write-in lines, where Text gets one. They were 60 underscores
      // against 30 — a difference nobody spots on a printed page, so "long
      // text" and "text" came out looking like the same field. The extra lines
      // are what actually says "there is room to write here".
      return withAnswers && hasAnswer(v)
        ? filled(v)
        : `${defText() || blank(60)}${LONG_TEXT_EXTRA_LINES}`;
    // Reached only from a COMPOSITE piece. A standalone Signature question is
    // intercepted in questionHtml and printed as its own block (role,
    // attestation, full-width rules) — that block cannot sit inline after a
    // piece label, so a signature bundled in with other answers prints as the
    // signature + date pair the block ends with.
    case "Signature":
      return `${blank(24)}<span class="hint">Signature</span>${blank(12)}<span class="hint">Date</span>`;
    default:
      return withAnswers && hasAnswer(v) ? filled(v) : defText() || blank(30);
  }
};

/**
 * A signature block — the attestation, then "<Role> Signature: ___ Date: ___".
 * Mirrors signatureParagraphs in sourceDocDocx: the role and the attestation
 * are part of what the investigator is signing, so neither may be dropped.
 */
const signatureHtml = (q) => {
  const role = q.signatureRole || "PI / Sub-I";
  const attest = q.attestation
    ? `<div class="attest">${esc(q.attestation)}</div>`
    : "";
  return `${attest}<div class="sig"><strong>${esc(
    role
  )} Signature: </strong>${blank(34)}<strong>   Date: </strong>${blank(16)}</div>`;
};

/**
 * The fixed closing block that ends EVERY source document — the Appointments
 * section and the staff/PI signatures that go with it. Appended
 * unconditionally, independent of any Signature questions in the template,
 * exactly as closingBlockElements does for Word. Without it the preview was
 * missing the whole last page of every document.
 *
 * It used to open with a second "PI/ Sub PI Signature:" and "Print Name:" pair
 * ABOVE the Appointments heading, which asked the investigator to sign the same
 * visit twice — once before the appointment block and once inside it. Only the
 * Appointments section's own sign-off is wanted. Mirrored in
 * closingBlockElements.
 */
const closingBlockHtml = () => {
  const sigLine = (label, len) =>
    `<div class="sig"><strong>${esc(label)} </strong>${blank(
      len
    )}<strong>   Date: </strong>${blank(18)}</div>`;

  return `
  <div class="closing">
    ${sectionHeadingHtml({ question: "Appointments" })}
    <div class="sig">Schedule for next appointment, ${blank(4)}/${blank(
    6
  )}/${blank(5)} at ${blank(5)}:${blank(6)}   Phone Visit</div>
    ${sigLine("Staff Signature:", 34)}
    ${sigLine("PI/ Sub PI Signature:", 40)}
  </div>`;
};

/**
 * What ends a LOG page instead of the block above.
 *
 * A log is signed off ONCE, when the study ends — not per visit, and it has no
 * next appointment to schedule. Printing the visit closing block on it would
 * ask for four signatures on a page that wants one. Mirrors logClosingElements
 * in sourceDocDocx.
 */
const logClosingHtml = () =>
  `<div class="closing"><div class="sig"><strong>${esc(
    LOG_CLOSING_LABEL
  )} </strong>${blank(38)}<strong>   Date</strong>${blank(5)}/${blank(
    6
  )}/${blank(7)}</div></div>`;

/**
 * What ends the ELIGIBILITY CHECKLIST — the initial screening review and the
 * final eligibility review, each with its attestation, its Yes/No and its two
 * signatures.
 *
 * Printed unconditionally, like the visit closing block, because it is what the
 * paper form ends with every time. The wording is data (ELIGIBILITY_CLOSING) so
 * this and criteriaClosingElements in sourceDocDocx render the same sentences —
 * an attestation is what someone is signing, and the two documents saying it
 * differently would be two different attestations.
 *
 * ONE top-level block, not several. Two reasons, and they are the same reason:
 * a signature is only meaningful next to the attestation it is signing, so the
 * sign-off must never be split across a page break. As a single block the print
 * CSS keeps it whole (`break-inside: avoid`) and the paginator moves it entire
 * onto the next page when it doesn't fit on this one.
 */
const criteriaClosingHtml = () =>
  `<div class="closing crit-closing">${ELIGIBILITY_CLOSING.map((b) => {
    if (b.kind === "heading")
      return `<div class="crit-head"><strong>${esc(b.text)}</strong></div>`;
    if (b.kind === "attestation")
      return `<div class="attest">${esc(b.text)}</div>`;
    if (b.kind === "question")
      return `<div class="crit-q"><span class="label">${esc(
        b.text
      )}</span><span class="answer"><span class="opts">${(b.options || [])
        .map((o) => choice(o))
        .join("")}</span></span></div>`;
    return `<div class="sig"><strong>${esc(b.label)} </strong>${blank(
      34
    )}<strong>   Date: </strong>${blank(18)}</div>`;
  }).join("")}</div>`;

/**
 * One table cell. A choice or tick-box column prints its boxes so the blank
 * form looks like the paper CRF it replaces; every other column keeps the
 * plain-text behaviour it has always had. `prefill` is a coordinator-set
 * default for a blank form — a patient's actual answer always wins over it
 * once real answers are printing. For a choice/tick-box column, a default
 * PRE-CHECKS that box rather than replacing the boxes with text — the boxes
 * are still the real answer choices, one is just ticked to start.
 */
const tableCellHtml = (c, raw, withAnswers, prefill) => {
  if (columnIsCheckbox(c)) {
    const checked = withAnswers ? isCheckedVal(raw) : !!prefill;
    return `<span class="cb">${mark(checked, "box")}</span>`;
  }
  if (columnPrintsBoxes(c)) {
    // cellSelection handles a MultiSelect default with several pre-checked
    // boxes (an array) the same way it handles a single-value default.
    const selected = withAnswers ? cellSelection(raw) : cellSelection(prefill);
    const style = markStyleFor(c);
    const marks = columnChoices(c).map((o) =>
      choice(o, selected.includes(String(o)), style)
    );
    // Stacked one per line when the column asks for it. Two options run
    // together in a narrow cell wrap wherever they happen to run out of room,
    // which is not the same thing as being on their own lines.
    return c.stack
      ? marks.map((m) => `<div class="cb-line">${m}</div>`).join("")
      : marks.join(" ");
  }
  // A STRUCTURAL column's prefill is printed FORM TEXT, not a pre-entered
  // answer, so it stays in ordinary weight and it prints on the patient's copy
  // too — see makeColumn. The eligibility checklist's criteria are this: a whole
  // page of them in bold would say every line had been answered in advance.
  if (c.structural) {
    const text = Array.isArray(prefill) ? prefill.join(", ") : prefill;
    if (text === undefined || text === null || text === "") return "";
    // A criterion carrying sub-parts prints them on their own indented lines,
    // as the protocol sets them out. See criterionLines.
    const lines = criterionLines(text);
    if (!lines) return esc(text);
    return lines
      .map(
        (l) =>
          `<div class="${l.indent ? "crit-sub" : "crit-lead"}">${esc(l.text)}</div>`
      )
      .join("");
  }
  if (!withAnswers && prefill) {
    const text = Array.isArray(prefill) ? prefill.join(", ") : prefill;
    return `<span class="prefill">${esc(text)}</span>`;
  }
  const text = Array.isArray(raw) ? raw.join(", ") : raw;
  return text === undefined || text === null || text === "" ? "" : esc(text);
};

// A composite question — several answer pieces bundled under one question via
// the field-type palette — is stored as a real Table (one row, no row
// labels; see isCompositeTable) so every renderer already handles its data,
// but it prints as a stack of labeled lines rather than a one-row grid.
//
// Each line is rendered by answerArea, the SAME renderer a standalone question
// of that type uses — not by tableCellHtml. A table cell is a box on a grid, so
// it draws no writing line and no format hint: the grid's ruling is the line
// and the column header carries the hint. A composite has neither, so routing
// its pieces through tableCellHtml printed a bold label followed by literally
// nothing for every write-in type. A Date lost "____ (DD-Mon-YYYY)", a Number
// lost its line and unit, a Long text lost all three of its lines — every
// write-in piece came out as the same bare row, which is the bug this fixes.
// Choice pieces (dropdown, checklist, Yes/No, checkbox) already drew their tick
// boxes and print exactly as they did before.
//
// answerArea reads a question's own `value`/`defaultValue`, while a composite
// keeps both a row deep (`value[0][colId]`, `prefillRows[0][colId]`), so each
// piece is lifted into the single-question shape answerArea expects.
// `unit` is dropped on purpose: a composite's own label already prints it
// ("Weight (kg):"), and answerArea would otherwise write it a second time
// after the line ("Weight (kg): ____ kg").
const compositePieceQuestion = (c, row, prefillRow) => ({
  ...c,
  unit: "",
  value: row[c.id],
  defaultValue: prefillRow[c.id],
});

const compositeTableHtml = (q, withAnswers) => {
  const cols = q.columns;
  const row = withAnswers ? (Array.isArray(q.value) ? q.value[0] : null) || {} : {};
  const prefillRow = (Array.isArray(q.prefillRows) && q.prefillRows[0]) || {};
  const lines = cols
    .map(
      (c) =>
        `<div class="composite-row"><span class="composite-label">${esc(
          c.label || "(untitled)"
        )}${
          c.unit ? ` (${esc(c.unit)})` : ""
        }:</span><span class="composite-value">${answerArea(
          compositePieceQuestion(c, row, prefillRow),
          withAnswers
        )}</span></div>`
    )
    .join("");
  return `<div class="composite-table">${lines}</div>`;
};

// Separator between the centred header/footer fields — the same run of spaces
// sourceDocDocx uses, so the two lines break identically.
const SEP = "&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;";

// Printable area of one page, derived from sourceDocDocx's TWIP table (1.1in
// top, 0.8in bottom, 0.55in sides). Landscape swaps the paper, not the margins,
// which is what the .docx does too — so the two documents keep breaking their
// lines and their pages in the same places.
//
//   portrait   8.5 x 11   ->  7.4in wide, 9.1in tall
//   landscape  11 x 8.5   ->  9.9in wide, 6.6in tall
const PORTRAIT_W_IN = 7.4;
const PORTRAIT_H_IN = 9.1;
const LANDSCAPE_W_IN = 9.9;
const LANDSCAPE_H_IN = 6.6;
const contentWIn = (landscape) => (landscape ? LANDSCAPE_W_IN : PORTRAIT_W_IN);
const contentHIn = (landscape) => (landscape ? LANDSCAPE_H_IN : PORTRAIT_H_IN);

// The narrow "#"/"No." lead column, in inches. 0.36in is INDEX_COL_TWIPS (520)
// from sourceDocDocx — the same column in both documents, the same width.
const INDEX_COL_IN = 0.36;

/**
 * The repeating page chrome — the two centred header rows and the centred
 * footer. One definition, used two ways: printed once into the single flowing
 * sheet, or cloned onto every page by the paginator.
 */
// Whether the header names a visit, and whether the footer counts pages, are
// TWO independent decisions that happened to agree for as long as a log was the
// only thing that wasn't a visit. The eligibility checklist splits them: it
// belongs to the study rather than to a visit (so no "Visit:", no "Visit Date:")
// but it is a finished document of known length (so a live "Page 2 of 3").
// Named here rather than re-derived, since both exporters have to agree.
const namesAVisit = ({ isLog, isCriteria }) => !isLog && !isCriteria;
const countsPages = ({ isLog }) => !isLog;
// A visit's footer date identifies the approved document a monitor checks a
// filed page against (see footerDateFor). The CHECKLIST is dated by the
// signatures on it — the screening review and the final eligibility review each
// carry their own date line — so a footer date is a second date to reconcile
// against those, and it prints without one.
const showsFooterDate = ({ isCriteria }) => !isCriteria;

/**
 * The study identity line that heads EVERY page: "BI: 1397-0014 AIRTIVITY".
 *
 * Byte-identical to what it always produced when all three fields are set. What
 * changes is a missing one: `${sponsor}: ${id} ${name}` wrote a bare leading
 * colon when the sponsor was blank and a trailing one when the study number and
 * name were, so a half-filled export config printed punctuation where the study
 * should have been — which reads as "this document names no study" rather than
 * "somebody hasn't filled the field in". Mirrored in sourceDocDocx.
 */
const studyLine = (c) => {
  const study = [c.studyId, c.studyName].map((s) => String(s || "").trim()).filter(Boolean).join(" ");
  const sponsor = String(c.sponsor || "").trim();
  if (sponsor && study) return `${sponsor}: ${study}`;
  return sponsor || study;
};

const docChromeHtml = (c, eventName = "", layout = {}) => `
    <div class="doc-header">
      <div class="hdr-row top">
        <span class="cell">${esc(studyLine(c))}${SEP}Site: ${esc(
  c.site || ""
)}${SEP}PI: ${esc(c.pi || "")}</span>
      </div>
      <div class="hdr-row pt">
        <span class="cell">${
          // Which visit this is, in the REPEATING header — the centred title
          // below it only appears on page one, so from page two onward a
          // filed-away page had nothing on it saying which visit it belonged
          // to.
          //
          // A LOG and the eligibility CHECKLIST carry neither this nor the visit
          // date below. Each is one standing document whose name is already the
          // heading on it, and neither has a single visit date to write. Both
          // were on them because they reuse the visit's chrome; neither means
          // anything there.
          //
          // The name prints on its own, with no "Visit:" in front of it — see
          // docHeader in sourceDocDocx, which this mirrors.
          namesAVisit(layout) && eventName
            ? `${esc(eventName)}${SEP}`
            : // The CHECKLIST names itself in the repeating header, the same way
              // a visit does. The centred title below only prints on page one,
              // so from page two onward a filed page said nothing about what
              // the boxes on it were for.
              layout.isCriteria
              ? `${esc(CRITERIA_PAGE_TITLE)}${SEP}`
              : ""
        }Patient Number: ${esc(c.patientPrefix || "")}${
  c.patientNumber ? filled(c.patientNumber) : blank(6)
}${SEP}Patient Initials: ${
  c.patientInitials ? filled(c.patientInitials) : blank(6)
}${namesAVisit(layout) ? `${SEP}Visit Date: ${blank(8)}` : ""}</span>
      </div>
    </div>
    <div class="doc-footer">
      <div class="ftr-row">${esc(c.version || "")}${
  // The CHECKLIST prints no date. A visit's footer date identifies the approved
  // document a monitor checks a filed page against; the checklist is dated by
  // the signatures on it, and a second date in the footer is one more thing to
  // reconcile. Every other document keeps it.
  c.date && showsFooterDate(layout) ? `${SEP}${esc(c.date)}` : ""
}${SEP}Page ${
  // A log is ONGOING: a coordinator photocopies the blank page as many times
  // as the study needs, so a page number printed now would be wrong on every
  // copy but the first. Blanks, filled in by hand. A visit — and the
  // eligibility checklist, which is printed once and filed — is a fixed
  // document and keeps its live count.
  countsPages(layout)
    ? `<span class="pg-n">1</span> of <span class="pg-t">1</span>`
    : `${blank(4)} of ${blank(4)}`
}</div>
    </div>`;

/**
 * Cuts the measured flow into real pages, each with its own header, footer and
 * "Page N of M". Runs in the preview only.
 *
 * It walks the TOP-LEVEL blocks and starts a new page when the next one would
 * not fit — the same unit the print CSS protects with `break-inside: avoid`,
 * so a question or a table row is never sliced down the middle here either.
 *
 * A block that is ITSELF taller than a page is the one case that needs more
 * than that. `.page-content` clips what overflows it, so a 34-row eligibility
 * grid simply lost its tail — the preview showed a document the print and the
 * .docx don't produce, and lost content is the worst possible failure for a page
 * whose whole job is to be checked against the protocol. Such a block is split
 * ROW-WISE, at the same boundary print and Word use: whole `<tbody>` rows, with
 * the grey `<thead>` cloned onto each part, so every page carries the table's
 * own header (and no criterion is ever cut in half). Only a too-tall block that
 * holds a table takes this path; everything that fits paginates exactly as it
 * always has.
 */
//
// THE CHECKLIST ALSO SPLITS A GRID THAT MERELY DOESN'T FIT WHAT'S LEFT
// (`splitToFill`). Everywhere else a block that fits a page but not the rest of
// this one moves to the next page whole, as it always has. On the eligibility
// checklist that left page one holding the title and one question over a page
// of white, with the inclusion grid starting on page two. So there, and only
// there, the grid starts under the question and carries on over the page, still
// cut only BETWEEN rows — a criterion is never split — and a sub-heading row
// ("Medical Conditions:") is never left alone at the foot of a page: it goes
// over with the criterion it introduces.
const paginateScript = (landscape, splitToFill = false) => `<script>
(function () {
  function paginate() {
    var flow = document.getElementById("doc-flow");
    var out = document.getElementById("doc-pages");
    var tpl = document.getElementById("doc-chrome");
    if (!flow || !out || !tpl) return;
    var limit = ${contentHIn(landscape)} * 96;
    var splitToFill = ${splitToFill ? "true" : "false"};

    function outerH(el) {
      var style = window.getComputedStyle(el);
      return el.getBoundingClientRect().height +
        parseFloat(style.marginTop || 0) + parseFloat(style.marginBottom || 0);
    }

    // Split one block around its table into as many blocks as it takes: the
    // first fits \`room\` (what is left of the current page), the rest fit a whole
    // page. Returns { parts, lastUsed } — lastUsed being how much of the final
    // part's page is spoken for, so whatever follows can still share it.
    // Returns null when this block can't be split that way, and the caller falls
    // back to the whole-block behaviour it has always had.
    function splitBlock(el, room) {
      var table = el.querySelector("table.grid");
      if (!table) return null;
      var tbody = table.tBodies && table.tBodies[0];
      if (!tbody || tbody.rows.length < 2) return null;

      // Everything in the block that ISN'T body rows — a caption, a label, and
      // the grey header band — is cloned onto every part, so it is charged to
      // each of them.
      var chromeH = outerH(el) - tbody.getBoundingClientRect().height;
      var rows = Array.prototype.slice.call(tbody.rows);
      var heights = rows.map(function (r) { return r.getBoundingClientRect().height; });
      // Nothing would fit in the remainder of this page, so don't start here.
      // On the checklist a grid can OPEN with a sub-heading; that heading needs
      // its first criterion beside it before this page counts as having room.
      var firstUnit = heights[0] +
        (splitToFill && rows[0].className === "grp" && rows.length > 1 ? heights[1] : 0);
      if (room < chromeH + firstUnit) room = limit;

      var parts = [];
      var body = null;
      var used = 0;
      var budget = room;
      for (var i = 0; i < rows.length; i++) {
        // Start a new part when this row won't fit — but never on a part that
        // is still empty: a row taller than a whole page can't be helped, so it
        // gets a part to itself and overflows, exactly as Word would have to.
        var carry = null;
        if (body && body.rows.length && used + heights[i] > budget) {
          // A sub-heading as the last row of a page introduces nothing on it.
          // Carry it over with its first criterion (checklist only).
          var lastRow = body.rows[body.rows.length - 1];
          if (splitToFill && body.rows.length > 1 && lastRow.className === "grp") {
            carry = lastRow;
            body.removeChild(carry);
          }
          body = null;
        }
        if (!body) {
          var part = el.cloneNode(true);
          body = part.querySelector("table.grid tbody");
          body.textContent = "";
          parts.push(part);
          used = chromeH;
          budget = parts.length === 1 ? room : limit;
          if (carry) {
            body.appendChild(carry);
            used += heights[i - 1];
          }
        }
        body.appendChild(rows[i].cloneNode(true));
        used += heights[i];
      }
      if (parts.length < 2) return null;
      return { parts: parts, lastUsed: used, startsOnNewPage: room === limit };
    }

    var blocks = Array.prototype.slice.call(flow.children);
    var pages = [[]];
    var used = 0;
    blocks.forEach(function (el) {
      var h = outerH(el);
      // Too tall for a whole page even on its own: split it row-wise rather
      // than let .page-content clip the rows off the end.
      // The checklist also splits a grid that only fails to fit the REST of this
      // page — see splitToFill above.
      if (h > limit || (splitToFill && used > 0 && used + h > limit)) {
        var split = splitBlock(el, limit - used);
        if (split) {
          split.parts.forEach(function (part, i) {
            if ((i === 0 && used > 0 && split.startsOnNewPage) || i > 0) {
              pages.push([]);
            }
            pages[pages.length - 1].push(part);
          });
          used = split.lastUsed;
          return;
        }
      }
      if (used > 0 && used + h > limit) { pages.push([]); used = 0; }
      pages[pages.length - 1].push(el);
      used += h;
    });
    out.textContent = "";
    pages.forEach(function (blocksOnPage, i) {
      var sheet = document.createElement("div");
      sheet.className = "sheet";
      var chrome = tpl.content.cloneNode(true);
      // A LOG's footer has no .pg-n/.pg-t to fill — it prints a blank
      // "Page __ of __" because the coordinator photocopies the page and
      // numbers it by hand. Guarded rather than assumed, or the preview
      // would throw on every log and render nothing at all.
      var pgN = chrome.querySelector(".pg-n");
      var pgT = chrome.querySelector(".pg-t");
      if (pgN) pgN.textContent = String(i + 1);
      if (pgT) pgT.textContent = String(pages.length);
      sheet.appendChild(chrome);
      var content = document.createElement("div");
      content.className = "page-content";
      blocksOnPage.forEach(function (el) { content.appendChild(el); });
      sheet.appendChild(content);
      out.appendChild(sheet);
    });
    flow.parentNode.removeChild(flow);
  }
  // Fonts change every measurement, so wait for them before cutting. Where
  // that promise isn't available, run as soon as the document is ready —
  // listening for "load" alone silently does nothing if load already fired.
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(paginate).catch(paginate);
  } else if (document.readyState === "complete") {
    paginate();
  } else {
    window.addEventListener("load", paginate);
  }
})();
</script>`;

// A table too wide to be written into on the page it is printing on is SPLIT
// into stacked sub-tables of at most CHUNK_DATA_COLS columns each, tied
// together by a numbered "#" column. How wide is "too wide" depends on the
// page: splitThresholdFor lives in templateSchema so this file and
// sourceDocDocx.js cannot disagree about it. CHUNK_DATA_COLS and the wording
// of the captions below are mirrored from sourceDocDocx.js for the same
// reason — the two exports have to lay a wide table out identically, or the
// preview would be showing a document nobody can download.
const CHUNK_DATA_COLS = 6;

/** One `<table class="grid">` for a set of columns. `withIndex` adds the
 * leading numbered column that lets a coordinator follow one row (e.g. AE #3)
 * across every sub-table of a split log. */
const gridHtml = (
  q,
  cols,
  withAnswers,
  withIndex = false,
  contentIn = PORTRAIT_W_IN,
  blankIndex = false
) => {
  // Fixed-row tables (per-system physical exam, a set lab panel) print their
  // row labels in a leading column, on the blank form as well as the filled
  // one — the labels are part of the form, not an answer.
  const rowLabels = Array.isArray(q.rowLabels) ? q.rowLabels : [];
  const fixedRows = rowLabels.length > 0;
  const prefillRows = Array.isArray(q.prefillRows) ? q.prefillRows : [];
  const leadHead = fixedRows
    ? `<th>${esc(q.rowLabelHeader || "")}</th>`
    : withIndex
    ? // "#" ties the chunks of a SPLIT table together; a log's own numbered
      // column is the same column with the form's own heading on it ("No.").
      `<th class="idx">${esc(q.numberHeader || "#")}</th>`
    : "";
  // One column's header text: its name, then any sublabel on its own line,
  // smaller and not bold — "(DD-MMM-YYYY)" under "End Date".
  // A column can mark ONE word of its header to print bold and underlined —
  // the "yes"/"no" that tells the two eligibility grids apart. Escaped in three
  // pieces so the emphasis tags are the only markup that survives.
  const labelHtml = (c) => {
    const parts = emphasisParts(c);
    if (!parts) return esc(c.label);
    const [before, hit, after] = parts;
    return `${esc(before)}<strong><u>${esc(hit)}</u></strong>${esc(after)}`;
  };
  const headText = (c) =>
    `${labelHtml(c)}${
      c.unit ? ` <span class="unit">(${esc(c.unit)})</span>` : ""
    }${c.sublabel ? `<div class="sub">${esc(c.sublabel)}</div>` : ""}`;

  // Spanning group headers ("Corticoids" over two columns) turn the header
  // into TWO rows: the groups on top, the column names beneath. An ungrouped
  // column spans both rows instead, so its name still sits beside the others.
  const groups = columnHeaderGroups(cols);
  let head;
  if (groups) {
    const top =
      (leadHead ? leadHead.replace("<th", '<th rowspan="2"') : "") +
      groups
        .map((g) =>
          g.label
            ? `<th colspan="${g.span}">${esc(g.label)}</th>`
            : `<th rowspan="2">${headText(cols[g.from])}</th>`
        )
        .join("");
    const second = groups
      .filter((g) => g.label)
      .flatMap((g) => cols.slice(g.from, g.from + g.span))
      .map((c) => `<th>${headText(c)}</th>`)
      .join("");
    head = `<tr>${top}</tr><tr>${second}</tr>`;
  } else {
    head = `<tr>${leadHead}${cols.map((c) => `<th>${headText(c)}</th>`).join("")}</tr>`;
  }
  // A log's own "No." column prints EMPTY — the paper form leaves the
  // coordinator to number their entries. The SPLIT path still prints 1..N,
  // because there the number is the only thing tying one row across the
  // stacked sub-tables.
  const lead = (ri, label) =>
    fixedRows
      ? `<td>${esc(label)}</td>`
      : withIndex
      ? `<td class="idx">${blankIndex ? "" : ri + 1}</td>`
      : "";

  // A sub-heading INSIDE the table — "Medical Conditions:" partway down an
  // exclusion list. One bold row spanning every column, drawn immediately BEFORE
  // the body row it is keyed to, so it takes no number of its own and shifts
  // nothing. Absent on every table that doesn't ask for one. See makeQuestion.
  const groupRows = q.groupRows && typeof q.groupRows === "object" ? q.groupRows : null;
  const spanAll = cols.length + (fixedRows || withIndex ? 1 : 0);
  const groupRow = (ri) =>
    groupRows && groupRows[ri]
      ? `<tr class="grp"><td colspan="${spanAll}">${esc(groupRows[ri])}</td></tr>`
      : "";

  // One data cell. A single tick box is centred in its column: the column is
  // only as wide as the box needs, and a box hugging the left edge of it reads
  // as belonging to the text in the cell before it.
  const cell = (c, raw, withAns, prefill) =>
    `<td${columnIsCheckbox(c) ? ' class="ctr"' : ""}>${tableCellHtml(
      c,
      raw,
      withAns,
      prefill
    )}</td>`;

  const rows = Array.isArray(q.value) ? q.value : [];
  let body;
  if (fixedRows) {
    body = rowLabels
      .map((label, ri) => {
        const r = withAnswers ? rows[ri] : null;
        const cells = cols
          .map((c) => cell(c, r ? r[c.id] : undefined, withAnswers, prefillRows[ri]?.[c.id]))
          .join("");
        return `${groupRow(ri)}<tr>${lead(ri, label)}${cells}</tr>`;
      })
      .join("");
  } else if (withAnswers && rows.length) {
    // Patient export — one row per recorded entry, cells addressed by column id.
    body = rows
      .map(
        (r, ri) =>
          `${groupRow(ri)}<tr>${lead(ri)}${cols
            .map((c) => cell(c, r ? r[c.id] : undefined, withAnswers))
            .join("")}</tr>`
      )
      .join("");
  } else {
    // A blank paper log needs rows to write in. Honour minRows, else give a
    // few — but never more than maxRows (tablePrintRowCount).
    //
    // These rows carry prefills too. A table's rows are numbered slots whether
    // or not they have names, so row 1 of a table with no rowLabels can hold a
    // coordinator-set starting value exactly like row 1 of a labelled one —
    // which is what the Word export has always done, and what the builder's
    // grid lets a coordinator type in.
    const n = tablePrintRowCount(q);
    body = Array.from({ length: n })
      .map(
        (_, ri) =>
          `${groupRow(ri)}<tr>${lead(ri)}${cols
            .map((c) => cell(c, undefined, false, prefillRows[ri]?.[c.id]))
            .join("")}</tr>`
      )
      .join("");
  }
  // Explicit column shares, when the table carries them (a paper log, whose
  // columns are nothing like equal). table-layout:fixed already honours a
  // <colgroup>, so this is all it takes.
  //
  // Everything is expressed as a percentage, including the narrow lead column,
  // rather than mixing a fixed 0.36in with percentages — under fixed layout
  // that mix sums past 100% and the browser rescales the lot, which is how the
  // widths would end up not being the widths. INDEX_COL_IN over the page's
  // content width is that same 0.36in, said in the units the rest is in, so
  // the data columns divide exactly what is left. Mirrors how the .docx
  // divides `dataW` after reserving INDEX_COL_TWIPS.
  //
  // A row-LABEL column normally holds words, so it takes a full share and is
  // left unsized here (fixed layout divides what's left evenly). A table that
  // sets rowLabelWidthPct is saying its label column holds a NUMBER — the
  // eligibility checklist's "#" — and names the share it should get, off the
  // top, exactly as the "#" column of a split table is reserved.
  const widths = columnWidthPercents(cols);
  const labelPct = Number(q.rowLabelWidthPct);
  const sizedLabel = fixedRows && Number.isFinite(labelPct) && labelPct > 0;
  let colgroup = "";
  if (widths) {
    const leadPct = sizedLabel
      ? labelPct
      : withIndex && !fixedRows
      ? (INDEX_COL_IN / contentIn) * 100
      : 0;
    const scale = (100 - leadPct) / 100;
    const leadCol = sizedLabel
      ? `<col style="width:${leadPct.toFixed(3)}%">`
      : fixedRows
      ? `<col>`
      : withIndex
      ? `<col style="width:${leadPct.toFixed(3)}%">`
      : "";
    colgroup = `<colgroup>${leadCol}${widths
      .map((w) => `<col style="width:${(w * scale).toFixed(3)}%">`)
      .join("")}</colgroup>`;
  }

  // Writing space per row, for a log filled in by hand. Absent on every other
  // table, which keeps the 16pt default it has always had.
  const rowH = tableRowHeightPt(q);
  const vars = [
    rowH !== DEFAULT_ROW_HEIGHT_PT ? `--row-h:${rowH}pt` : "",
    // A standing log's headers ARE the form, so they print larger than the
    // 9pt a visit's inline grid uses.
    q.headerFontPt ? `--head-f:${q.headerFontPt}pt` : "",
  ].filter(Boolean);
  const style = vars.length ? ` style="${vars.join(";")}"` : "";

  return `<table class="grid"${style}>${colgroup}<thead>${head}</thead><tbody>${body}</tbody></table>`;
};

const tableHtml = (q, withAnswers = false, landscape = false) => {
  const cols = Array.isArray(q.columns) ? q.columns : [];
  if (!cols.length) return `<div class="hint">(table — no columns)</div>`;
  if (isCompositeTable(q)) return compositeTableHtml(q, withAnswers);

  const contentIn = contentWIn(landscape);

  // Fits the page — one table. `numbered` asks for the lead column here too:
  // a paper log's "No." column is the same column the split path uses to tie
  // its chunks together, just wanted on a table that isn't being split.
  if (cols.length <= splitThresholdFor(landscape)) {
    return gridHtml(q, cols, withAnswers, q.numbered === true, contentIn, true);
  }

  // Too wide to write into: stacked sub-tables, exactly as Word builds them.
  const chunks = [];
  for (let i = 0; i < cols.length; i += CHUNK_DATA_COLS) {
    chunks.push(cols.slice(i, i + CHUNK_DATA_COLS));
  }
  const caption = (t) => `<div class="tbl-caption">${esc(t)}</div>`;
  return (
    caption(
      `This log has ${cols.length} columns — shown as ${chunks.length} tables below. The # column is the same row in each.`
    ) +
    chunks
      .map((chunk, ci) => {
        const from = ci * CHUNK_DATA_COLS + 1;
        const to = ci * CHUNK_DATA_COLS + chunk.length;
        return (
          caption(`Columns ${from}–${to}${ci ? " (continued)" : ""}`) +
          gridHtml(q, chunk, withAnswers, true, contentIn)
        );
      })
      .join("")
  );
};

/**
 * A section title — the shaded, boxed, upper-cased band Word prints, with the
 * sponsor's optional red instruction ("Prior to other testing") pushed to the
 * right edge. Mirrors sectionTitleParagraph.
 */
const sectionHeadingHtml = (q) =>
  `<h3 class="section"><span>${esc(q.question || "Section")}</span>${
    q.instruction ? `<span class="instruction">${esc(q.instruction)}</span>` : ""
  }</h3>`;

/**
 * Should this question print without its own label?
 *
 * Only on a LOG, and only when the label is exactly the log's name. The centred
 * bold title at the top of the page already says it, so printing it again puts
 * the same words twice in a row under the heading. The label still has to
 * EXIST — validateTemplate rejects a question with no text — it just doesn't
 * print. Scoped to logs so nothing about a visit's rendering changes.
 */
const labelIsPageTitle = (q, { isLog, isCriteria, eventName }) => {
  const label = String(q.question || "").trim();
  if (!label) return false;
  if (isLog && eventName) return label === String(eventName).trim();
  // On the CHECKLIST a grid's title is carried by its own Criteria column
  // header — which is the point, since a table header repeats at the top of
  // every page the grid runs onto. Printing the same words as a bold label
  // above the grid as well says it twice on page one and not at all after.
  if (isCriteria) {
    const first = Array.isArray(q.columns) ? q.columns[0] : null;
    return !!first && String(first.label || "").trim() === label;
  }
  return false;
};

const questionHtml = (q, labelById, withAnswers = false, layout = {}) => {
  if (q.type === "Header") return sectionHeadingHtml(q);

  const req = q.required ? ` <span class="req">*</span>` : "";
  const cond = conditionText(q.visibleWhen, labelById);
  const condNote = cond ? `<span class="cond">only if ${cond}</span>` : "";

  if (q.type === "Signature") {
    return `<div class="q sig-q">${signatureHtml(q)}</div>`;
  }
  if (q.type === "Table") {
    const label = labelIsPageTitle(q, layout)
      ? ""
      : `<div class="label">${esc(q.question)}${req}${condNote}</div>`;
    // On a log, a grid that follows a question needs a line of air between the
    // two — without it the table butts straight up against the text and reads
    // as part of it. Only when something precedes it: the log whose table is
    // the first thing on the page must not be pushed down, since its row count
    // is sized to the page.
    const spaced = layout.spacedTable ? " spaced" : "";
    return `<div class="q table-q${spaced}">${label}${tableHtml(
      q,
      withAnswers,
      layout.landscape
    )}</div>`;
  }
  // Label and answer share ONE line, wrapping together — Word puts the answer
  // runs inside the label's own paragraph, so a field is one row of the form,
  // not two.
  return `<div class="q"><span class="label">${esc(
    q.question
  )}${req}${condNote}</span><span class="answer">${answerArea(
    q,
    withAnswers
  )}</span></div>`;
};

/**
 * Build the full printable HTML for one visit's source document.
 *
 * @param {object} args
 * @param {object} args.event   one event (visit) of the assembled template
 * @param {object} args.config  { sponsor, studyId, studyName, site, pi, version, date, patientPrefix }
 * @param {boolean} [args.withAnswers]  render recorded answers (patient export)
 *                  instead of a blank form. Defaults to false — a blank form.
 * @param {boolean} [args.showPrintButton]  draw the floating "Print / Save as
 *                  PDF" button. On for the standalone tab, off when this page
 *                  is embedded in the in-app preview, which has its own.
 * @param {boolean} [args.paginate]  cut the flow into real letter pages on
 *                  SCREEN, each with its own header, footer and "Page N of M".
 *                  For the in-app preview, whose whole job is to look like the
 *                  exported document — which has pages. Off for the printable
 *                  tab, where the browser does its own pagination and a second
 *                  one layered on top would fight it.
 * @returns {string} a complete, self-contained HTML document
 */
export const buildSourceDocHtml = ({
  event,
  config = {},
  withAnswers = false,
  showPrintButton = true,
  paginate = false,
}) => {
  const questions = (event && event.questions) || [];
  // Map question id -> label so a conditional field can name what controls it.
  const labelById = new Map(questions.map((q) => [q.id, q.question]));

  // A standing log prints differently from a visit in four ways — landscape
  // paper, a blank "Page __ of __", a "Log:" header and a single sign-off line
  // instead of the visit closing block. All four are decided here, from the
  // event, and threaded down; nothing below re-derives them.
  const eventName = (event && event.eventName) || "";
  const isLog = eventIsLog(event);
  // The eligibility checklist — a visit's live page count and a log's
  // study-level header. See namesAVisit / countsPages.
  const isCriteria = eventIsCriteria(event);
  const landscape = eventIsLandscape(event);
  const layout = { isLog, isCriteria, landscape, eventName };

  const body = questions
    .filter((q) => q.type === "Header" || !isSourceOnlyType(q.type) || q.type === "Signature")
    .map((q, i) =>
      questionHtml(q, labelById, withAnswers, {
        ...layout,
        // A standing page's grid gets a line of air above it when something
        // precedes it — see the Table branch in questionHtml. On the checklist
        // that is what separates the inclusion grid from the exclusion one.
        spacedTable: (isLog || isCriteria) && q.type === "Table" && i > 0,
      })
    )
    .join("\n");

  const c = config;
  const chrome = docChromeHtml(c, eventName, layout);
  const sheetW = landscape ? 11 : 8.5;
  const sheetH = landscape ? 8.5 : 11;

  // Header repeats on every printed page via position:fixed; the body is padded
  // to clear it. thead { table-header-group } repeats table headers per page.
  return `<!doctype html><html><head><meta charset="utf-8">
<title>Source Document — ${esc(event && event.eventName)}</title>
<style>
  * { box-sizing: border-box; }
  /* Calibri 10pt — the .docx's own default run. Everything below is sized in
     points against it, because a Word "size" is half-points: 26 -> 13pt,
     22 -> 11pt, 20 -> 10pt, 18 -> 9pt, 16 -> 8pt. Matching the type is what
     makes the two documents break their lines in the same places. */
  html, body { margin: 0; font-family: Calibri, "Segoe UI", Arial, sans-serif;
               color: #111; font-size: 10pt; line-height: 1.25;
               overflow-wrap: break-word; }
  /* Page geometry lifted from sourceDocDocx's TWIP table: 1.1in top, 0.8in
     bottom, 0.55in sides, header 0.4in and footer 0.3in into those margins. */
  @page { size: letter${landscape ? " landscape" : ""}; margin: 1.1in 0.55in 0.8in; }
  .doc-header { position: fixed; top: -0.7in; left: 0; right: 0; }
  .doc-footer { position: fixed; bottom: -0.5in; left: 0; right: 0; }
  /* Centered, both rows — Word centres the whole header and footer rather than
     pushing the fields to opposite edges. */
  .hdr-row { text-align: center; }
  .hdr-row.top { font-weight: 700; border-bottom: 1.5pt solid #111; padding-bottom: 2pt; }
  .hdr-row.pt { padding-bottom: 3pt; border-bottom: 0.5pt solid #999; }
  /* No nowrap: Word's header is one centred paragraph that wraps when the
     study name is long, and pinning it to a single line here would push the
     PI field off the edge of a page the .docx has no trouble with. */
  .hdr-row .cell { max-width: 100%; overflow-wrap: break-word; }
  .doc-footer .ftr-row { text-align: center; font-size: 8pt; color: #444;
                         border-top: 0.5pt solid #999; padding-top: 2pt; }
  .visit-title { text-align: center; margin: 0 0 8pt; font-size: 13pt; font-weight: 700; }
  /* On a LOG this line is the page heading the paper form prints in caps —
     "CONCOMITANT MEDICATION LOG" — so it is upper-cased here rather than
     shouted in the editor's dropdowns, where the log is named normally. */
  body.log .visit-title, body.criteria .visit-title { text-transform: uppercase; }
  /* The checklist's sign-off block. Everything below is scoped to
     .crit-closing so the visit and log closing blocks keep the spacing they
     have always had.

     It is ONE block that must not be split: a signature means nothing apart
     from the attestation above it. break-inside:avoid keeps it whole in print,
     and the preview's paginator moves it whole for the same reason. */
  .crit-closing { break-inside: avoid; page-break-inside: avoid; margin-top: 18pt; }
  /* Room to actually sign. These are hand-written on a printed page, so the
     line needs vertical space around it, not just a rule to sit on. */
  .crit-closing .crit-head { margin: 20pt 0 4pt; font-size: 11pt; }
  .crit-closing .crit-head:first-child { margin-top: 0; }
  .crit-closing .attest { margin: 4pt 0 10pt; }
  .crit-closing .crit-q { margin: 10pt 0 12pt; }
  .crit-closing .crit-q .label { font-weight: 700; }
  .crit-closing .crit-q .answer { margin-left: 1.5em; }
  .crit-closing .sig { margin: 18pt 0 4pt; }
  /* The shaded, boxed, upper-cased band Word prints for a section title, with
     the sponsor's red instruction at the right edge. */
  h3.section { display: flex; justify-content: space-between; gap: 8pt; align-items: baseline;
               background: #D9D9D9; border: 0.5pt solid #111; padding: 1.5pt 4pt;
               margin: 11pt 0 4pt; font-size: 11pt; font-weight: 700;
               text-transform: uppercase; break-after: avoid; }
  h3.section .instruction { color: #C00000; font-weight: 700; font-size: 10pt;
                            text-align: right; overflow-wrap: break-word; }
  .q { padding: 2pt 0 1pt; break-inside: avoid; border-bottom: 0.5pt dotted #DDDDDD; }
  /* A line of air between a question and the grid below it — see questionHtml.
     12pt is one line of the 10pt body type. */
  .q.table-q.spaced { margin-top: 12pt; }
  /* The checklist's criteria grids flow over the page instead of jumping whole
     to the next one, which left page one empty under the opening question.
     Rows still never split (tr { break-inside: avoid } below) and the grey
     header repeats on every page. Checklist only — every other grid keeps
     .q's break-inside: avoid. */
  body.criteria .q.table-q { break-inside: auto; page-break-inside: auto; }
  .q .label { font-weight: 700; }
  .req { color: #C00000; font-weight: 700; }
  .cond { margin-left: 6pt; font-weight: 400; font-style: italic; color: #555; font-size: 9pt; }
  /* Inline, on the label's own line — see questionHtml. */
  .answer { margin-left: 1.5em; }
  /* Nothing may exceed the width of the page. Word wraps a long label, a long
     option or a long cell onto the next line and carries on; anything here that
     could push past the sheet edge instead has to wrap the same way, or the
     preview shows an overflow the exported document does not have. */
  .blank { letter-spacing: 0.02em; }
  .filled { font-weight: 700; overflow-wrap: break-word; }
  /* A row of choices wraps as a row; an individual choice keeps its mark and
     its label together, but a label too long for one line still breaks rather
     than running off the page. */
  .opts { display: inline-flex; flex-wrap: wrap; align-items: baseline; vertical-align: top; }
  .opts .cb { margin-right: 12pt; max-width: 100%; overflow-wrap: break-word; }
  .unit { color: #555; margin-left: 3pt; }
  .hint { color: #888; font-size: 8pt; margin-left: 4pt; font-style: italic; }
  /* The continuation lines of a Long text field, full width under the first. */
  .long-line { letter-spacing: 0.02em; overflow: hidden; white-space: nowrap; }
  .attest { font-style: italic; font-size: 9pt; margin: 6pt 0 1pt; }
  .sig { margin: 6pt 0 1pt; }
  .closing { margin-top: 12pt; }
  /* table-layout: fixed is what matches Word, which sizes a table's columns
     from an explicit grid and wraps inside the cells. Left to its own devices
     (auto) the browser instead grows the table past 100% to fit the widest
     cell content, which is exactly the text that ran off the page here while
     the .docx of the same table was fine. */
  table.grid { width: 100%; table-layout: fixed; border-collapse: collapse; margin-top: 2pt; }
  table.grid th, table.grid td { border: 0.5pt solid #111; padding: 2pt 3pt; text-align: left; vertical-align: top; overflow-wrap: break-word; }
  /* A standing log's headers carry the only instructions on the page, so they
     print larger than a visit's inline grid; gridHtml sets --head-f from the
     question's headerFontPt. Centred and padded, so the header row reads as a
     band rather than as another row of the table. */
  table.grid th { background: #D9D9D9; font-size: var(--head-f, 9pt);
                  padding: 4pt 3pt; text-align: center; vertical-align: middle; }
  /* A column's second header line — the date format, the "if yes" prompt.
     Smaller and NOT bold, so it reads as a note under the name rather than as
     part of it. */
  table.grid th .sub { font-weight: 400; font-size: 0.8em; color: #333;
                       margin-top: 1pt; }
  /* A sub-heading INSIDE the table ("Medical Conditions:"), spanning every
     column. Bold and shaded a shade lighter than the header band, so it reads
     as a divider rather than as another entry to be ticked. */
  table.grid tr.grp td { font-weight: 700; background: #F2F2F2; }
  /* A single tick box sits in the middle of its narrow column. */
  table.grid td.ctr { text-align: center; }
  /* A criterion's sub-parts, set out on their own lines under its lead-in the
     way the protocol writes them. 14pt of indent, matching the 280 twips the
     .docx uses, so the two documents hang the sub-parts at the same place.
     The marker ("a.", "•") is the protocol's own and is part of the text. */
  table.grid td .crit-lead { margin: 0; }
  table.grid td .crit-sub { margin: 1pt 0 0 14pt; }
  /* One choice per line in a cell that asked for them stacked. */
  table.grid td .cb-line { display: block; }
  table.grid td .cb-line .cb { margin-right: 0; }
  /* One line of writing space, unless the table asks for more — a standing log
     is filled in with a pen and needs room for a drug name. gridHtml sets
     --row-h from the question's rowHeightPt; every other table has no such
     variable and keeps the 16pt it has always had. */
  table.grid td { height: var(--row-h, 16pt); }
  /* 0.36in is INDEX_COL_TWIPS (520) from sourceDocDocx — the "#" column is a
     cross-reference number in both documents and must be the same width in
     both. A row-LABEL column is deliberately NOT this: it holds words, so it
     takes a full share here (fixed layout divides evenly) and, since the same
     fix landed in the .docx, over there too. */
  table.grid th.idx, table.grid td.idx { width: 0.36in; text-align: center; color: #555; }
  .tbl-caption { font-style: italic; color: #555; font-size: 8pt; margin: 5pt 0 1pt; break-after: avoid; }
  .prefill { font-weight: 700; }
  /* Word prints a composite question as a STACK OF LABELLED LINES, not a
     grid — see compositeTableElements. No border, no cells. */
  .composite-row { padding: 2pt 0 1pt; }
  .composite-label { font-weight: 700; }
  .composite-value { margin-left: 0.5em; }
  thead { display: table-header-group; }
  tr { break-inside: avoid; }
  .toolbar { position: fixed; top: 8px; right: 8px; }
  .toolbar button { font: inherit; padding: 6px 12px; cursor: pointer; }
  @media print { .toolbar { display: none; } }

  /* On SCREEN the same markup is drawn as a real letter sheet — grey desk,
     white page, the printed margins as actual padding, header and footer in
     the flow at the top and bottom of that page. Without this the page-margin
     positioning that makes the header repeat on every PRINTED page (a fixed
     element sitting at a negative offset, i.e. up in the margin) pins it to
     the top of the window and hangs it half off the screen, which is why the
     printable page never looked like the document until it was printed.
     Print media is untouched: none of these rules apply there. */
  @media screen {
    body { background: #6b7280; padding: 22px 12px; }
    /* Exactly one page wide — never shrunk to fit the window. A narrower sheet
       would wrap text at a width the printed page never uses, which is the one
       thing this preview exists to get right. On a window too narrow for a
       letter page the preview scrolls sideways instead. */
    .sheet { width: ${sheetW}in; min-height: ${sheetH}in; margin: 0 auto 22px;
             background: #fff; padding: 1.1in 0.55in 0.8in;
             box-shadow: 0 10px 40px rgba(0,0,0,0.45); position: relative; }
    /* The header and footer sit IN the page margins, at the same offsets Word
       uses (header 0.4in from the top edge, footer 0.3in from the bottom). */
    .sheet > .doc-header { position: absolute; top: 0.4in; left: 0.55in; right: 0.55in; }
    .sheet > .doc-footer { position: absolute; bottom: 0.3in; left: 0.55in; right: 0.55in; }
  }

  /* Paginated preview only: the flow is measured and cut into real pages, each
     carrying its own header and footer, so where a page ENDS is visible. Word
     is what it is mirroring, and Word has pages. */
  @media screen {
    .paged .flow { position: absolute; left: -10000px; top: 0; width: ${contentWIn(
      landscape
    )}in; visibility: hidden; }
    .paged .page-content { height: ${contentHIn(landscape)}in; overflow: hidden; }
  }
</style></head>
<body class="${[
    paginate ? "paged" : "",
    isLog ? "log" : "",
    isCriteria ? "criteria" : "",
  ]
    .filter(Boolean)
    .join(" ")}">
  ${
    showPrintButton
      ? `<div class="toolbar"><button onclick="window.print()">Print / Save as PDF</button></div>`
      : ""
  }
  <template id="doc-chrome">${chrome}</template>
  <div class="sheet ${paginate ? "flow" : ""}" id="doc-flow">
    ${paginate ? "" : chrome}
    <div class="visit-title">${esc(
      // The checklist's page-one heading is what the document is CALLED, which
      // is the same name its repeating header carries — not the section name it
      // happens to be filed under in the editor.
      isCriteria ? CRITERIA_PAGE_TITLE : eventName
    )}</div>
    ${body}
    ${isLog ? logClosingHtml() : isCriteria ? criteriaClosingHtml() : closingBlockHtml()}
  </div>
  ${paginate ? `<div id="doc-pages"></div>${paginateScript(landscape, isCriteria)}` : ""}
</body></html>`;
};

/**
 * Which `studies/{id}` document a template's header should be prefilled from.
 *
 * A template's own id is USUALLY the study id — but a built one lives at
 * `{studyId}__assembled` (see JoinReviewPage / SectionEditor's promote flow),
 * and `studies/{studyId}__assembled` does not exist. Reading it silently
 * returned nothing, so every built source document opened its export and its
 * preview with a blank Sponsor / Study # / Study name / Site header and the
 * coordinator re-typed all four by hand.
 *
 * `sourceStudyId` is the explicit answer where a template carries one; the
 * suffix rule is the fallback, and it is the same rule the approve/promote path
 * already uses to find the canonical template.
 *
 * @param {object} [template]  the template doc being exported
 * @param {string} [templateId]  the id it was opened at
 * @returns {string} the real study document id
 */
export const exportStudyIdFor = (template, templateId = "") =>
  template?.sourceStudyId || String(templateId).replace(/__assembled$/, "");

/**
 * Sensible export config from a study doc + any saved overrides on the template.
 * Study fields (SPONSOR/STUDY_ID/NAME/SITE_ID) prefill the identifiers; PI and
 * version are the human-entered bits.
 */
export const defaultExportConfig = (study = {}, saved = {}, todayStr = "") => ({
  sponsor: saved.sponsor ?? study.SPONSOR ?? "",
  studyId: saved.studyId ?? study.STUDY_ID ?? "",
  studyName: saved.studyName ?? study.NAME ?? "",
  site: saved.site ?? study.SITE_ID ?? "",
  patientPrefix: saved.patientPrefix ?? (study.SITE_ID ? `${study.SITE_ID}-` : ""),
  // Sandeep Bansal, MD is currently the only PI, so default to him rather
  // than making every export re-type it. `||`, not `??`: a template that was
  // exported before this default existed has an empty string saved, not
  // undefined, and that blank should still fall back to the default.
  pi: saved.pi || "Sandeep Bansal, MD",
  version: saved.version ?? "Version 1.0",
  date: saved.date ?? todayStr,
});

// ---------------------------------------------------------------------------
// The footer date
//
// It is the date THIS VISIT WAS APPROVED, not the date someone hit export.
// The footer's job is to identify the document — "Version 2.0 · 09-Aug-2026"
// is the pair a monitor checks a filed page against, and a date that moved
// every time anybody re-printed the same approved form made that pair
// meaningless.
//
// A visit that has never been approved has no such date, so it prints as a
// DRAFT with today's date on it: a draft that leaves the building must not be
// mistakable for the approved source document.
// ---------------------------------------------------------------------------

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** DD-Mon-YYYY — the format already on every exported document. */
export const formatDocDate = (value) => {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return `${String(d.getDate()).padStart(2, "0")}-${MONTHS[d.getMonth()]}-${d.getFullYear()}`;
};

/**
 * What the footer prints for a visit.
 *
 * @param {object} event  the visit, carrying `approvedAt` once approved
 * @param {Date|string} [fallback]  the date a DRAFT is stamped with — today
 *   for a live export, the snapshot's own date when exporting from history
 * @returns {string} "09-Aug-2026", or "DRAFT 26-Aug-2026"
 */
export const footerDateFor = (event, fallback = new Date()) => {
  const approved = event?.approvedAt ? formatDocDate(event.approvedAt) : "";
  if (approved) return approved;
  const date = formatDocDate(fallback);
  // A visit approved before approvals carried their own date (see
  // backfillEventApproval) is still an APPROVED document — we just don't know
  // when. It falls back to an undated print rather than being stamped DRAFT,
  // which would be a false statement about a live source document.
  if (event?.status === "approved") return date;
  return date ? `DRAFT ${date}` : "DRAFT";
};
